"use client";

import { useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import type { PyloMedia } from "@/services/pylo-media.service";
import { MEDIA_KIND_ICONS, fileExtensionOf, mediaKindOf } from "./media-utils";

interface MediaThumbProps {
  /** Structural so an in-flight upload can render through an object URL. */
  media: Pick<PyloMedia, "url" | "file_mime_type"> & Partial<PyloMedia>;
  className?: string;
  iconClassName?: string;
}

/**
 * There is no thumbnail pipeline yet, so an image thumb loads the full original
 * behind its signed URL. `unoptimized` keeps the Next optimizer out of it — the
 * signature makes every URL unique, so optimizing would re-run on every render
 * and never hit cache.
 */
export const MediaThumb = ({ media, className, iconClassName }: MediaThumbProps) => {
  const [failed, setFailed] = useState(false);
  const kind = mediaKindOf(media.file_mime_type);
  const Icon = MEDIA_KIND_ICONS[kind];
  const showImage = kind === "image" && media.url && !failed;

  if (showImage) {
    return (
      <Image
        src={media.url}
        alt={media.file_name ?? "Preview"}
        fill
        unoptimized
        sizes="200px"
        className={cn("object-cover", className)}
        onError={() => setFailed(true)}
      />
    );
  }

  const extension = fileExtensionOf(media);

  return (
    <div
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground",
        className,
      )}
    >
      <Icon className={cn("h-7 w-7", iconClassName)} />
      {extension && <span className="text-[10px] font-medium tracking-wide">{extension}</span>}
    </div>
  );
};
